import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  socioApi, authApi,
  type SocioMe, type VestingStatus, type TokenHolding, ApiError,
} from "@/api/client";
import { keystore } from "@/keystore";
import { AppShell } from "@/components/AppShell";
import { formatTokenAmount } from "@/lib/format";

// Vesting ainda usa 18 decimais (os contratos de vesting são do token
// principal). Mantém um formatador simples pra ele.
function formatBigInt(value: string | undefined, decimals = 18): string {
  return formatTokenAmount(value, decimals);
}

function dateFromUnix(s: number | undefined): string {
  if (!s) return "—";
  return new Date(s * 1000).toLocaleDateString("pt-BR");
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [socio, setSocio] = useState<SocioMe | null>(null);
  const [vesting, setVesting] = useState<VestingStatus | null>(null);
  const [holdings, setHoldings] = useState<TokenHolding[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [contractsMissing, setContractsMissing] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!keystore.isUnlocked()) {
      navigate("/login");
      return;
    }
    refresh();
  }, []);

  async function refresh() {
    setLoading(true);
    setError(null);
    setContractsMissing([]);
    try {
      const me = await socioApi.me();
      setSocio(me);
      const missing: string[] = [];
      const vs = await socioApi.vesting().catch((err) => {
        if (err instanceof ApiError && err.status === 503) {
          missing.push("VestingFactory");
          return null;
        }
        throw err;
      });
      if (vs) setVesting(vs);
      const bal = await socioApi.balances().catch((err) => {
        if (err instanceof ApiError && err.status === 503) {
          missing.push("GoyazToken");
          return null;
        }
        throw err;
      });
      if (bal) setHoldings(bal.tokens);
      setContractsMissing(missing);
    } catch (err: any) {
      setError(err.message ?? "Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    keystore.lock();
    await authApi.logout();
    navigate("/login");
  }

  if (loading) {
    return (
      <AppShell>
        <div className="p-8 text-center text-app-muted">Carregando...</div>
      </AppShell>
    );
  }

  // Vesting é do token principal do projeto. Usa o símbolo do primeiro
  // holding; se a carteira ainda não tem saldo, cai num rótulo neutro.
  const symbol = holdings[0]?.tokenSymbol ?? "tokens";

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-6">
        <header className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Olá, {socio?.nomeCompleto.split(" ")[0]}</h1>
            <p className="text-sm text-app-muted">{socio?.cargo || socio?.role}</p>
          </div>
          <div className="flex gap-4 text-sm">
            <Link to="/settings" className="underline hover:text-app-accent">Configurações</Link>
            <button onClick={logout} className="text-red-500 hover:text-red-400 underline">Sair</button>
          </div>
        </header>

        {error && (
          <div className="bg-red-500/10 border border-red-500/40 text-red-500 rounded p-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {contractsMissing.length > 0 && (
          <div className="app-card border-amber-500/40 bg-amber-500/5 p-4 mb-6">
            <p className="font-semibold mb-1">⚙️ Contratos ainda não deployados</p>
            <p className="text-sm text-app-muted">
              Aguardando deploy de: <code className="font-mono">{contractsMissing.join(", ")}</code>.
              {socio?.role === "ADMIN" && (
                <>
                  {" "}
                  <Link to="/admin/projects/new" className="text-app-accent underline font-medium">
                    Crie uma sociedade
                  </Link>{" "}
                  pra deployar tudo.
                </>
              )}
            </p>
          </div>
        )}

        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="app-card p-4">
            <h3 className="text-xs uppercase tracking-wide text-app-muted mb-1">Smart Wallet</h3>
            <p className="font-mono text-xs break-all">{socio?.smartAccount || "—"}</p>
          </div>
          <div className="app-card p-4">
            <h3 className="text-xs uppercase tracking-wide text-app-muted mb-2">
              {holdings.length > 1 ? "Seus tokens" : "Saldo"}
            </h3>
            {holdings.length === 0 ? (
              <p className="text-3xl font-semibold">0</p>
            ) : (
              <ul className="space-y-2">
                {holdings.map((t) => (
                  <li key={t.tokenAddress} className="flex items-baseline justify-between gap-2">
                    <span className="text-2xl font-semibold">
                      {formatTokenAmount(t.balance, t.decimals)}
                      <span className="text-sm font-normal text-app-muted ml-1">{t.tokenSymbol}</span>
                    </span>
                    <span className="text-xs text-app-muted whitespace-nowrap">
                      voto: {formatTokenAmount(t.votingPower, t.decimals)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="app-card p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">Meu Vesting</h2>
          {vesting?.hasVesting ? (
            <div className="space-y-2 text-sm">
              <div className="bg-app-surface-2 rounded p-3 mb-3">
                <p className="text-xs uppercase tracking-wide text-app-muted">Total em vesting</p>
                <p className="text-3xl font-semibold">{formatBigInt(vesting.total)} <span className="text-base font-normal text-app-muted">{symbol}</span></p>
                <p className="text-xs text-app-muted mt-1">
                  {formatBigInt(vesting.locked)} bloqueado · {formatBigInt(vesting.releasable)} liberado · {formatBigInt(vesting.released)} já sacado
                </p>
              </div>
              <Row label="Início" value={dateFromUnix(vesting.start)} />
              <Row label="Duração total"
                   value={`${vesting.duration ? Math.round(vesting.duration / 86400 / 30) : "?"} meses`} />
              <Row label="Cliff"
                   value={`${vesting.cliffDuration ? Math.round(vesting.cliffDuration / 86400 / 30) : "?"} meses`} />
              <Row label="Ainda bloqueado" value={`${formatBigInt(vesting.locked)} ${symbol}`} />
              <Row label="Já sacado" value={`${formatBigInt(vesting.released)} ${symbol}`} />
              <Row label="Disponível pra sacar"
                   value={`${formatBigInt(vesting.releasable)} ${symbol}`}
                   bold />
              {vesting.revoked && (
                <p className="text-red-500 text-xs mt-2">Vesting revogado</p>
              )}
              {!vesting.revoked && vesting.releasable && BigInt(vesting.releasable) > 0n && (
                <Link to="/claim"
                      className="block bg-app-accent text-app-accent-text rounded py-2 mt-4 text-center font-medium hover:opacity-90">
                  Reivindicar tokens
                </Link>
              )}
            </div>
          ) : (
            <p className="text-sm text-app-muted">Sem vesting ativo pra esta wallet.</p>
          )}
        </section>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ActionLink to="/send" label={`Enviar ${symbol}`} />
          <ActionLink to="/proposals" label="Propostas" />
          <ActionLink to="/organizacao" label="Organização" />
          <ActionLink to="/atividade" label="Atividade on-chain" />
          {socio?.role === "ADMIN" && (
            <>
              <ActionLink to="/admin/projects" label="Projetos" highlight />
              <ActionLink to="/admin/deploy" label="Deploy avançado" />
              <ActionLink to="/admin/activity" label="Atividade (tudo)" />
            </>
          )}
        </section>
      </div>
    </AppShell>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between border-b border-app-border last:border-0 py-1">
      <span className="text-app-muted">{label}:</span>
      <span className={bold ? "font-semibold" : ""}>{value}</span>
    </div>
  );
}

function ActionLink({ to, label, highlight }: { to: string; label: string; highlight?: boolean }) {
  return (
    <Link to={to}
          className={`rounded py-3 text-center text-sm transition border ${
            highlight
              ? "bg-app-accent text-app-accent-text border-app-accent hover:opacity-90 font-medium"
              : "bg-app-surface border-app-border text-app-text hover:bg-app-surface-2"
          }`}>
      {label}
    </Link>
  );
}
